import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { hexToHsl, hslToHex, createGradientStop, sortGradientStops, generateGradientCss, DEFAULT_GRADIENT } from './colorUtils';

/**
 * GradientPicker - A modern UI for creating and editing linear and radial gradients
 *
 * @param {Object} props
 * @param {Object} props.value - Current gradient value
 * @param {string} props.value.type - 'linear-gradient' or 'radial-gradient'
 * @param {number} [props.value.angle] - Angle for linear gradient (0-360)
 * @param {string} [props.value.shape] - Shape for radial gradient ('circle' or 'ellipse')
 * @param {string} [props.value.position] - Position for radial gradient
 * @param {Array} props.value.stops - Array of color stops
 * @param {Function} props.onChange - Callback when gradient changes
 */
export default function GradientPicker({ value, onChange }) {
  // Ensure we have a valid value
  const gradientValue = useMemo(() => {
    if (!value || !value.stops || value.stops.length < 2) {
      return { ...DEFAULT_GRADIENT };
    }
    return value;
  }, [value]);

  // Selected stop index for editing
  const [selectedStopIndex, setSelectedStopIndex] = useState(0);

  // Refs for drag handling
  const gradientBarRef = useRef(null);
  const anglePickerRef = useRef(null);
  const hueSliderRef = useRef(null);
  const alphaSliderRef = useRef(null);
  const isDraggingRef = useRef(null);
  const dragStopIndexRef = useRef(null);

  // Checkered pattern for transparency backgrounds
  const checkeredBg = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill-opacity='.1'%3E%3Crect x='0' y='0' width='8' height='8' fill='%23808080'/%3E%3Crect x='8' y='8' width='8' height='8' fill='%23808080'/%3E%3C/svg%3E")`;

  // Get current selected stop
  const selectedStop = gradientValue.stops[selectedStopIndex] || gradientValue.stops[0];

  // Parse selected stop color to HSL
  const selectedStopHsl = useMemo(() => {
    const hsl = hexToHsl(selectedStop?.color || '#000000');
    return hsl || { h: 0, s: 100, l: 50 };
  }, [selectedStop?.color]);

  // Common angle presets for linear gradient
  const anglePresets = [0, 45, 90, 135, 180, 225, 270, 315];

  // Radial gradient position options
  const positionOptions = [
    { value: 'center', label: 'Center' },
    { value: 'top', label: 'Top' },
    { value: 'bottom', label: 'Bottom' },
    { value: 'left', label: 'Left' },
    { value: 'right', label: 'Right' },
    { value: 'top left', label: 'Top Left' },
    { value: 'top right', label: 'Top Right' },
    { value: 'bottom left', label: 'Bottom Left' },
    { value: 'bottom right', label: 'Bottom Right' },
  ];

  // Update gradient value
  const updateGradient = useCallback(
    (updates) => {
      onChange({ ...gradientValue, ...updates });
    },
    [gradientValue, onChange]
  );

  // Update a specific stop
  const updateStop = useCallback(
    (index, updates) => {
      const newStops = [...gradientValue.stops];
      newStops[index] = { ...newStops[index], ...updates };
      updateGradient({ stops: sortGradientStops(newStops) });

      // Update selected index if positions changed
      const sortedStops = sortGradientStops(newStops);
      const newIndex = sortedStops.findIndex(
        (s) =>
          s.color === newStops[index].color &&
          s.position === newStops[index].position
      );
      if (newIndex !== -1 && newIndex !== index) {
        setSelectedStopIndex(newIndex);
      }
    },
    [gradientValue.stops, updateGradient]
  );

  // Add a new stop
  const addStop = useCallback(
    (position = 50) => {
      if (gradientValue.stops.length >= 10) return;

      // Find colors of adjacent stops for interpolation
      const sortedStops = sortGradientStops(gradientValue.stops);
      let leftStop = sortedStops[0];

      for (let i = 0; i < sortedStops.length - 1; i++) {
        if (sortedStops[i].position <= position && sortedStops[i + 1].position >= position) {
          leftStop = sortedStops[i];
          break;
        }
      }

      // Use the left stop color for the new stop
      const newStop = createGradientStop(leftStop.color, 100, position);
      const newStops = sortGradientStops([...gradientValue.stops, newStop]);
      updateGradient({ stops: newStops });

      // Select the new stop
      const newIndex = newStops.findIndex((s) => s.position === position);
      setSelectedStopIndex(newIndex !== -1 ? newIndex : 0);
    },
    [gradientValue.stops, updateGradient]
  );

  // Delete a stop
  const deleteStop = useCallback(
    (index) => {
      if (gradientValue.stops.length <= 2) return;

      const newStops = gradientValue.stops.filter((_, i) => i !== index);
      updateGradient({ stops: sortGradientStops(newStops) });

      // Adjust selected index if needed
      if (selectedStopIndex >= newStops.length) {
        setSelectedStopIndex(newStops.length - 1);
      } else if (selectedStopIndex > index) {
        setSelectedStopIndex(selectedStopIndex - 1);
      }
    },
    [gradientValue.stops, selectedStopIndex, updateGradient]
  );

  // Handle gradient bar click to add stop
  const handleGradientBarClick = useCallback(
    (e) => {
      if (isDraggingRef.current) return;

      const rect = gradientBarRef.current?.getBoundingClientRect();
      if (!rect) return;

      const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      const position = Math.round(x * 100);

      // Check if clicking near an existing stop
      const nearStop = gradientValue.stops.some(
        (stop) => Math.abs(stop.position - position) < 5
      );

      if (!nearStop) {
        addStop(position);
      }
    },
    [gradientValue.stops, addStop]
  );

  // Handle stop drag
  const handleStopDrag = useCallback(
    (clientX) => {
      const rect = gradientBarRef.current?.getBoundingClientRect();
      if (!rect || dragStopIndexRef.current === null) return;

      const x = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      const position = Math.round(x * 100);

      updateStop(dragStopIndexRef.current, { position });
    },
    [updateStop]
  );

  // Handle angle drag
  const handleAngleDrag = useCallback(
    (clientX, clientY) => {
      const rect = anglePickerRef.current?.getBoundingClientRect();
      if (!rect) return;

      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;

      const deltaX = clientX - centerX;
      const deltaY = clientY - centerY;

      // Calculate angle (0 = top, clockwise)
      let angle = Math.atan2(deltaX, -deltaY) * (180 / Math.PI);
      angle = ((angle % 360) + 360) % 360;
      angle = Math.round(angle);

      updateGradient({ angle });
    },
    [updateGradient]
  );

  // Handle hue slider drag
  const handleHueSliderDrag = useCallback(
    (clientX) => {
      const rect = hueSliderRef.current?.getBoundingClientRect();
      if (!rect) return;

      const x = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      const newHue = Math.round(x * 360);

      const newColor = hslToHex(newHue, selectedStopHsl.s, selectedStopHsl.l);
      updateStop(selectedStopIndex, { color: newColor });
    },
    [selectedStopHsl, selectedStopIndex, updateStop]
  );

  // Handle alpha slider drag
  const handleAlphaSliderDrag = useCallback(
    (clientX) => {
      const rect = alphaSliderRef.current?.getBoundingClientRect();
      if (!rect) return;

      const x = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      const newAlpha = Math.round(x * 100);

      updateStop(selectedStopIndex, { alpha: newAlpha });
    },
    [selectedStopIndex, updateStop]
  );

  // Mouse event handlers
  const handleMouseDown = useCallback(
    (e, type, stopIndex = null) => {
      e.preventDefault();
      e.stopPropagation();
      isDraggingRef.current = type;

      if (type === 'stop' && stopIndex !== null) {
        dragStopIndexRef.current = stopIndex;
        setSelectedStopIndex(stopIndex);
        handleStopDrag(e.clientX);
      } else if (type === 'angle') {
        handleAngleDrag(e.clientX, e.clientY);
      } else if (type === 'hue') {
        handleHueSliderDrag(e.clientX);
      } else if (type === 'alpha') {
        handleAlphaSliderDrag(e.clientX);
      }
    },
    [handleStopDrag, handleAngleDrag, handleHueSliderDrag, handleAlphaSliderDrag]
  );

  const handleMouseMove = useCallback(
    (e) => {
      if (!isDraggingRef.current) return;

      if (isDraggingRef.current === 'stop') {
        handleStopDrag(e.clientX);
      } else if (isDraggingRef.current === 'angle') {
        handleAngleDrag(e.clientX, e.clientY);
      } else if (isDraggingRef.current === 'hue') {
        handleHueSliderDrag(e.clientX);
      } else if (isDraggingRef.current === 'alpha') {
        handleAlphaSliderDrag(e.clientX);
      }
    },
    [handleStopDrag, handleAngleDrag, handleHueSliderDrag, handleAlphaSliderDrag]
  );

  const handleMouseUp = useCallback(() => {
    isDraggingRef.current = null;
    dragStopIndexRef.current = null;
  }, []);

  // Add global mouse listeners for drag
  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchmove', (e) => {
      if (isDraggingRef.current && e.touches[0]) {
        handleMouseMove({ clientX: e.touches[0].clientX, clientY: e.touches[0].clientY });
      }
    });
    document.addEventListener('touchend', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  // Handle touch events
  const handleTouchStart = useCallback(
    (e, type, stopIndex = null) => {
      e.preventDefault();
      const touch = e.touches[0];
      isDraggingRef.current = type;

      if (type === 'stop' && stopIndex !== null) {
        dragStopIndexRef.current = stopIndex;
        setSelectedStopIndex(stopIndex);
        handleStopDrag(touch.clientX);
      } else if (type === 'angle') {
        handleAngleDrag(touch.clientX, touch.clientY);
      } else if (type === 'hue') {
        handleHueSliderDrag(touch.clientX);
      } else if (type === 'alpha') {
        handleAlphaSliderDrag(touch.clientX);
      }
    },
    [handleStopDrag, handleAngleDrag, handleHueSliderDrag, handleAlphaSliderDrag]
  );

  // Handle hex input change
  const handleHexInput = useCallback(
    (e) => {
      let value = e.target.value;
      if (!value.startsWith('#')) {
        value = '#' + value;
      }

      // Only update if valid hex
      if (/^#[0-9A-Fa-f]{6}$/.test(value)) {
        updateStop(selectedStopIndex, { color: value.toUpperCase() });
      }
    },
    [selectedStopIndex, updateStop]
  );

  // Handle position input change
  const handlePositionInput = useCallback(
    (e) => {
      const value = Math.max(0, Math.min(100, parseInt(e.target.value, 10) || 0));
      updateStop(selectedStopIndex, { position: value });
    },
    [selectedStopIndex, updateStop]
  );

  // Handle angle input change
  const handleAngleInput = useCallback(
    (e) => {
      const value = ((parseInt(e.target.value, 10) || 0) % 360 + 360) % 360;
      updateGradient({ angle: value });
    },
    [updateGradient]
  );

  // Generate CSS for preview
  const gradientCss = useMemo(() => generateGradientCss(gradientValue), [gradientValue]);

  return (
    <div className="space-y-4">
      {/* Gradient Type Toggle */}
      <div className="flex p-1 bg-gray-100 rounded-lg">
        <button
          type="button"
          onClick={() => updateGradient({ type: 'linear-gradient', angle: gradientValue.angle || 90 })}
          className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
            gradientValue.type === 'linear-gradient'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Linear
        </button>
        <button
          type="button"
          onClick={() =>
            updateGradient({
              type: 'radial-gradient',
              shape: gradientValue.shape || 'ellipse',
              position: gradientValue.position || 'center',
            })
          }
          className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
            gradientValue.type === 'radial-gradient'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Radial
        </button>
      </div>

      {/* Gradient Preview Bar */}
      <div className="space-y-2">
        <div
          ref={gradientBarRef}
          className="relative h-8 rounded-lg cursor-pointer select-none overflow-hidden"
          style={{
            backgroundImage: checkeredBg,
            backgroundColor: '#fff',
          }}
          onClick={handleGradientBarClick}
        >
          {/* Gradient overlay */}
          <div
            className="absolute inset-0 rounded-lg"
            style={{ background: gradientCss }}
          />
        </div>

        {/* Color Stops */}
        <div className="relative h-6">
          {sortGradientStops(gradientValue.stops).map((stop, index) => (
            <div
              key={index}
              className="absolute -translate-x-1/2 cursor-pointer group"
              style={{ left: `${stop.position}%` }}
            >
              {/* Stop handle */}
              <div
                className={`relative w-4 h-4 rounded-full border-2 shadow-md transition-transform ${
                  selectedStopIndex === index
                    ? 'border-blue-500 scale-110 z-10'
                    : 'border-white hover:scale-105'
                }`}
                style={{
                  backgroundColor: stop.color,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                }}
                onMouseDown={(e) => handleMouseDown(e, 'stop', index)}
                onTouchStart={(e) => handleTouchStart(e, 'stop', index)}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedStopIndex(index);
                }}
              />

              {/* Delete button */}
              {gradientValue.stops.length > 2 && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteStop(index);
                  }}
                  className="absolute -top-5 left-1/2 -translate-x-1/2 w-4 h-4 bg-red-500 text-white rounded-full text-xs leading-none opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 flex items-center justify-center"
                  title="Delete stop"
                >
                  <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Add Stop Button */}
        {gradientValue.stops.length < 10 && (
          <button
            type="button"
            onClick={() => addStop(50)}
            className="w-full px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded-md hover:bg-gray-100 transition-colors flex items-center justify-center gap-1"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Color Stop
          </button>
        )}
      </div>

      {/* Selected Stop Editor */}
      {selectedStop && (
        <div className="p-3 bg-gray-50 rounded-lg space-y-3">
          <div className="flex items-center gap-2 text-xs font-medium text-gray-700">
            <div
              className="w-4 h-4 rounded border border-gray-300"
              style={{ backgroundColor: selectedStop.color }}
            />
            <span>Stop {selectedStopIndex + 1}</span>
          </div>

          {/* Hue Slider */}
          <div
            ref={hueSliderRef}
            className="relative w-full h-3 rounded cursor-pointer select-none"
            style={{
              background:
                'linear-gradient(to right, #ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000)',
            }}
            onMouseDown={(e) => handleMouseDown(e, 'hue')}
            onTouchStart={(e) => handleTouchStart(e, 'hue')}
          >
            <div
              className="absolute w-3 h-5 -translate-x-1/2 -translate-y-1/4 rounded border-2 border-white shadow-md pointer-events-none"
              style={{
                left: `${(selectedStopHsl.h / 360) * 100}%`,
                backgroundColor: hslToHex(selectedStopHsl.h, 100, 50),
                boxShadow: '0 0 0 1px rgba(0,0,0,0.3), 0 2px 4px rgba(0,0,0,0.2)',
              }}
            />
          </div>

          {/* Alpha Slider */}
          <div
            ref={alphaSliderRef}
            className="relative w-full h-3 rounded cursor-pointer select-none"
            style={{
              backgroundImage: checkeredBg,
              backgroundColor: '#fff',
            }}
            onMouseDown={(e) => handleMouseDown(e, 'alpha')}
            onTouchStart={(e) => handleTouchStart(e, 'alpha')}
          >
            <div
              className="absolute inset-0 rounded pointer-events-none"
              style={{
                background: `linear-gradient(to right, transparent, ${selectedStop.color})`,
              }}
            />
            <div
              className="absolute w-3 h-5 -translate-x-1/2 -translate-y-1/4 rounded border-2 border-white shadow-md pointer-events-none"
              style={{
                left: `${selectedStop.alpha}%`,
                backgroundColor: selectedStop.color,
                boxShadow: '0 0 0 1px rgba(0,0,0,0.3), 0 2px 4px rgba(0,0,0,0.2)',
              }}
            />
          </div>

          {/* Stop Controls */}
          <div className="flex gap-2">
            <div className="flex-1">
              <input
                type="text"
                value={selectedStop.color}
                onChange={handleHexInput}
                className="w-full px-2 py-1 text-xs border border-gray-200 rounded bg-white focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
                placeholder="#000000"
              />
              <label className="block text-[10px] text-gray-400 mt-0.5 text-center">
                HEX
              </label>
            </div>
            <div className="w-16">
              <input
                type="number"
                value={selectedStop.alpha}
                onChange={(e) =>
                  updateStop(selectedStopIndex, {
                    alpha: Math.max(0, Math.min(100, parseInt(e.target.value, 10) || 0)),
                  })
                }
                min={0}
                max={100}
                className="w-full px-2 py-1 text-xs border border-gray-200 rounded bg-white focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
              />
              <label className="block text-[10px] text-gray-400 mt-0.5 text-center">
                Alpha %
              </label>
            </div>
            <div className="w-16">
              <input
                type="number"
                value={selectedStop.position}
                onChange={handlePositionInput}
                min={0}
                max={100}
                className="w-full px-2 py-1 text-xs border border-gray-200 rounded bg-white focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
              />
              <label className="block text-[10px] text-gray-400 mt-0.5 text-center">
                Position %
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Linear Gradient Controls */}
      {gradientValue.type === 'linear-gradient' && (
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            {/* Visual Angle Picker */}
            <div
              ref={anglePickerRef}
              className="relative w-16 h-16 rounded-full bg-gray-100 border border-gray-200 cursor-pointer select-none flex-shrink-0"
              onMouseDown={(e) => handleMouseDown(e, 'angle')}
              onTouchStart={(e) => handleTouchStart(e, 'angle')}
            >
              {/* Center dot */}
              <div className="absolute top-1/2 left-1/2 w-2 h-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-gray-400" />

              {/* Direction line */}
              <div
                className="absolute top-1/2 left-1/2 w-0.5 h-6 bg-blue-500 rounded-full origin-bottom"
                style={{
                  transform: `translate(-50%, -100%) rotate(${gradientValue.angle || 90}deg)`,
                }}
              >
                {/* Arrow head */}
                <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-blue-500 rounded-full" />
              </div>

              {/* Degree markers */}
              {[0, 90, 180, 270].map((deg) => (
                <div
                  key={deg}
                  className="absolute text-[8px] text-gray-400 font-medium"
                  style={{
                    top: deg === 0 ? '2px' : deg === 180 ? 'auto' : '50%',
                    bottom: deg === 180 ? '2px' : 'auto',
                    left: deg === 270 ? '2px' : deg === 90 ? 'auto' : '50%',
                    right: deg === 90 ? '2px' : 'auto',
                    transform:
                      deg === 0 || deg === 180
                        ? 'translateX(-50%)'
                        : 'translateY(-50%)',
                  }}
                >
                  {deg}
                </div>
              ))}
            </div>

            {/* Angle Input */}
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <input
                  type="number"
                  value={gradientValue.angle || 90}
                  onChange={handleAngleInput}
                  min={0}
                  max={360}
                  className="w-20 px-2 py-1 text-xs border border-gray-200 rounded bg-gray-50 focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
                />
                <span className="text-xs text-gray-500">degrees</span>
              </div>

              {/* Angle Presets */}
              <div className="flex flex-wrap gap-1">
                {anglePresets.map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    onClick={() => updateGradient({ angle: preset })}
                    className={`px-2 py-0.5 text-[10px] font-medium rounded transition-colors ${
                      gradientValue.angle === preset
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {preset}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Radial Gradient Controls */}
      {gradientValue.type === 'radial-gradient' && (
        <div className="space-y-3">
          {/* Shape Selector */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Shape</label>
            <div className="flex gap-2">
              {['circle', 'ellipse'].map((shape) => (
                <button
                  key={shape}
                  type="button"
                  onClick={() => updateGradient({ shape })}
                  className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md border transition-all capitalize ${
                    gradientValue.shape === shape
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {shape}
                </button>
              ))}
            </div>
          </div>

          {/* Position Selector */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Position</label>
            <div className="grid grid-cols-3 gap-1">
              {positionOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => updateGradient({ position: option.value })}
                  className={`px-2 py-1.5 text-[10px] font-medium rounded transition-colors ${
                    gradientValue.position === option.value
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
